import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { ReactElement, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BaseMap, Layer, PickingInfo, ViewStateChangeParameters } from '@methanesat/maps';
import { Box, useTheme } from '@methanesat/ui-components';

import { GoogleBasemaps, maxZoomBaseMap, minZoomBaseMap, PICKING_RADIUS_PIXELS } from '../../consts';
import { GOOGLE_MAPS_API_KEY } from '../../environmentVariables';
import {
    useGetAllTargetsWithNumberOfCaptures,
    useGetCapturesWithinGlobalTimeRange,
    useGetCapturesWithinViewport,
    useTranslate
} from '../../hooks';
import {
    useDetailsSelectionIcon,
    useInfrastructureTileLayer,
    useMethaneRasterLayers,
    usePlumeEmissionRateLayer,
    useSyncQueryString,
    useTargetLayers,
    useThrottledUpdateBBox
} from '../../hooks/pages/emissions-map';
import {
    selectEmissionsDrawerCardData,
    selectEmissionsMapName,
    selectGlobalDate,
    selectL4MSATColorScale,
    selectMethaneProduct,
    selectPlatform,
    selectUrlParamsInitialized,
    setMethaneLayerConfig,
    SLOW_selectEmissionsMapViewState
} from '../../reducers';
import type { RootState } from '../../store';
import {
    AreaEmissionsProducts,
    CardUrlState,
    isPlumeEmissionInfo,
    MethaneLayerIds,
    MSatPickInfo,
    Platforms,
    UrlState,
    UrlViewState
} from '../../types';
import { DeckGLRefWithWidth } from '../../types/DeckGLExtension';
import { CapturePlusCount, GoogleBasemapNames } from '../../types';
import { formatDateForUrl, getRasterColorMapName, getHoveredPlumeId, QueryStringObject } from '../../utils';
import { onViewStateChangeHelper } from './util';

/**
 * Emissions maps props.
 */
export interface EmissionsPageMapProps {
    useOnMapClick: (
        deck: RefObject<DeckGLRefWithWidth | null>,
        layers: Layer[],
        visibleCaptures: CapturePlusCount[]
    ) => (info: MSatPickInfo) => Promise<void>;
}

/**
 * Emissions map gives context to where methane plumes are, who's emitting them, and how much is being emitted.
 */
function EmissionsPageMap({ useOnMapClick }: EmissionsPageMapProps): ReactElement {
    const deck = useRef<DeckGLRefWithWidth | null>(null);
    const dispatch = useDispatch();
    const t = useTranslate();
    const theme = useTheme();
    const colorScheme = theme.palette.mode;

    // platform
    const platform = useSelector(selectPlatform);

    // date selected
    const globalDate = useSelector(selectGlobalDate);
    const globalDateObject = useMemo(() => {
        return new Date(globalDate);
    }, [globalDate]);

    // viewState
    const viewState = useSelector(SLOW_selectEmissionsMapViewState);

    const mapName = useSelector(selectEmissionsMapName);
    const gestureHandling = useSelector((state: RootState) => state.pages.emissions.mapInterface.gestureHandling);

    const [isInteracting, setIsInteracting] = useState(false);

    // if the data is L3 or L4
    const product = useSelector(selectMethaneProduct);
    const { featureList } = useGetCapturesWithinGlobalTimeRange(product);

    const visibleCaptures = useGetCapturesWithinViewport(featureList);

    // color scale name
    const l4MSATColorScale = useSelector(selectL4MSATColorScale);
    const colorScaleName = getRasterColorMapName(platform, product, l4MSATColorScale);

    // methane layers
    // l4 layers
    const methaneRasterLayers = useMethaneRasterLayers(featureList, isInteracting);
    const plumeEmissionRateLayer = usePlumeEmissionRateLayer(featureList);

    // target layer
    const targetLayer = useTargetLayers(viewState.zoom, featureList);

    // selected emissions details layers
    const selectedEmissionsDetailsLayer = useDetailsSelectionIcon();

    const tileInfrastructureLayer = useInfrastructureTileLayer();

    // array of deck.gl layers passed to map
    // These are ordered by the order in which they should be stacked by deck.gl:
    // layers early in the array are stacked below those later in the array
    const layers: Layer[] = [
        ...methaneRasterLayers,
        plumeEmissionRateLayer,
        selectedEmissionsDetailsLayer,
        tileInfrastructureLayer,
        targetLayer
    ].filter((a) => !!a) as Layer[];

    // function to update the URL no more than once every 500 ms
    // all state-related URL updates are throttled, not just the ones that *must* be throttled
    // because there is no down-side to throttling all updates
    const debouncedRouterReplace = useMemo(
        // switching to useMemo as useCallback expects an inline function
        // useMemo will still return a memoized debounced function
        () =>
            debounce(
                (newQuery: QueryStringObject) => {
                    const searchArray: string[] = [];
                    // Add or update parameters based on the new state
                    Object.entries(newQuery).forEach(([key, value]) => {
                        if (value !== '') {
                            if (Array.isArray(value)) {
                                searchArray.push(`${key}=${value.join(',')}`);
                            } else {
                                searchArray.push(`${key}=${value}`);
                            }
                        }
                    });
                    const queryStr = searchArray.join('&');
                    history.replaceState({ ...history.state }, '', `?${queryStr}`);
                },
                500,
                {
                    trailing: true
                }
            ),
        []
    );

    const targetsWithNumberOfCaptures = useGetAllTargetsWithNumberOfCaptures();

    const getTooltip = useCallback(
        (info: PickingInfo) => {
            const infoLayer = info.layer;
            if (!infoLayer) return null;
            if (!info.object) return null;

            const captureCount = targetsWithNumberOfCaptures.get(
                info.object.properties.target_group_id || info.object.properties.target_id
            );
            if ((infoLayer.id === 'targets' || infoLayer.id === 'targetsmultiple-capture-icon') && captureCount > 1) {
                return (
                    info.object && {
                        text: t('targetTooltip', {
                            captureCount
                        }),
                        style: {
                            position: 'absolute',
                            top: '-50px',
                            left: '-35px',
                            borderRadius: '2px',
                            color: '#ffffff',
                            fontSize: '16px'
                        }
                    }
                );
            }
        },
        [t, targetsWithNumberOfCaptures]
    );

    const dateString = useMemo(() => {
        return formatDateForUrl(globalDateObject);
    }, [globalDateObject]);

    const cleanViewState: UrlViewState = useMemo(() => {
        const { latitude, longitude, zoom } = viewState;
        return { latitude, longitude, zoom: zoom.toFixed(2) };
    }, [viewState]);

    const emissionsDrawerCardData = useSelector(selectEmissionsDrawerCardData);

    const urlParamsInitialized = useSelector(selectUrlParamsInitialized);

    // List defining the state useSyncQueryString should add to & extract from the URL's querystring
    // Also defines how to handle each querystring parameter's value on page load
    const urlStateItems = useMemo(() => {
        const baseState = {
            // generic URL parameters
            view: cleanViewState,
            date: dateString,
            platform,
            product,
            'color-scale':
                product === AreaEmissionsProducts.l4 && platform === Platforms.MSAT ? colorScaleName : undefined
        } as UrlState;

        // parameters for identifying selected target or emissions
        // additional parameters for identifying selected emissions
        let updatedEmissionsDrawerCardData: CardUrlState = {} as CardUrlState;
        if (urlParamsInitialized && emissionsDrawerCardData) {
            updatedEmissionsDrawerCardData = structuredClone(emissionsDrawerCardData);
        }
        return {
            ...baseState,
            ...updatedEmissionsDrawerCardData
        };
    }, [cleanViewState, dateString, platform, product, colorScaleName, emissionsDrawerCardData, urlParamsInitialized]);

    useSyncQueryString({
        urlStateItems,
        dispatch,
        urlUpdateFn: debouncedRouterReplace
    });

    const throttledUpdateBBox = useThrottledUpdateBBox(dispatch);

    const onMapClick = useOnMapClick(deck, layers, visibleCaptures);

    const onHover = throttle(
        (info: PickingInfo) => {
            const infosUnderCursor = deck.current?.pickMultipleObjects({
                x: info.x,
                y: info.y,
                radius: PICKING_RADIUS_PIXELS,
                depth: layers.length
            });

            // Set the hovered plume in redux. This is used in the infrastructure layer to prevent
            // highlighting OGI if there is a plume underneath it.
            const hoveredPlume = infosUnderCursor?.find((i) => isPlumeEmissionInfo(i as MSatPickInfo));
            dispatch(
                setMethaneLayerConfig({
                    layerId: MethaneLayerIds.plumeEmissionRate,
                    layerUpdates: { hoveredPlumeId: getHoveredPlumeId(hoveredPlume?.object) }
                })
            );
        },
        75,
        { leading: true, trailing: true }
    );

    // handler for viewStateChange event on Deck
    function onViewStateChange({ viewState }: { viewState: ViewStateChangeParameters['viewState'] }) {
        onViewStateChangeHelper({ viewState, dispatch, throttledUpdateBBox });
    }

    // Triggered in `DeckGLOverlay` during map drag, zoom, and idle event listeners.
    const onInteractionStateChange = (interactionState: Partial<ViewStateChangeParameters['interactionState']>) => {
        const { isDragging, isZooming } = interactionState;
        if (isDragging || isZooming) {
            setIsInteracting(true);
        } else {
            setIsInteracting(false);
        }
    };

    return (
        <>
            {/*
             * Adding a grayscale filter to images in the basemap makes the
             * Google base map gray without changing the colors of the
             * deck.gl layers. The filter values were arrived at by testing
             * various combinations with Product.
             */}
            <Box
                id="emissions-map-google-container"
                data-testid="emissions-map-google-container"
                component="span"
                sx={{
                    ...(mapName == GoogleBasemapNames.Roadmap
                        ? {}
                        : {
                              '& .gm-style img': {
                                  filter: 'grayscale(0.97) brightness(1.13) contrast(1.14) opacity(0.96) saturate(4.26)'
                              }
                          })
                }}
            >
                <BaseMap
                    id="emissions-map-google"
                    data-testid="emissions-map-google"
                    gestureHandling={gestureHandling}
                    googleMapsAPIKey={GOOGLE_MAPS_API_KEY}
                    viewState={{
                        ...viewState
                    }}
                    onViewStateChange={onViewStateChange}
                    onInteractionStateChange={onInteractionStateChange}
                    layers={layers}
                    onClick={(info) => onMapClick(info as MSatPickInfo)}
                    onHover={onHover}
                    ref={deck}
                    maxZoom={maxZoomBaseMap}
                    minZoom={minZoomBaseMap}
                    getTooltip={getTooltip}
                    mapId={GoogleBasemaps[mapName].id}
                    mapType={GoogleBasemaps[mapName].type}
                    colorScheme={colorScheme}
                />
            </Box>
        </>
    );
}

export default EmissionsPageMap;
